import {
  ReportsFindAllAnimalType,
  ReportsFindAllCategoryOfTest,
  ReportsFindAllStatus,
  ReportsFindAllSurveillanceType,
  ReportsFindAllWildType,
} from '@api/WhisApi/generated/model';
import type { ReferentialsResponseDto } from '@api/WhisApi/generated/model/referentialsResponseDto';
import type { SelectOption } from '@components/Select/select.types';
import {
  buildEtiologicalAgentOptions,
  buildEtiologicalAgentOptionsByEtiologyType,
  buildEtiologyTypeOptions,
  buildSampleTypeOptions,
} from '@screens/ReportCreation/forms/referentialOptions';
import type { FormOption } from '@screens/ReportCreation/reportCreation.types';
import { getLocalizedLabel } from '@screens/UserManagement/userManagement.utils';
import type { SupportedLanguage } from '@store/i18n';
import type { TFunction } from 'i18next';
import type {
  ReportFiltersConfig,
  ReportFiltersSection,
} from './reportFilters.types';

const buildAllToggleOption = (t: TFunction): FormOption => ({
  value: '',
  label: t('filters.options.all'),
});

const toToggleOptions = (t: TFunction, options: FormOption[]): FormOption[] => [
  buildAllToggleOption(t),
  ...options,
];

const buildCountryOptions = (
  language: SupportedLanguage,
  referentials?: ReferentialsResponseDto,
): SelectOption[] =>
  referentials?.countries?.map((country) => ({
    value: country.code,
    label: getLocalizedLabel(country.name, language) || country.code,
  })) ?? [];

const buildRegionOptions = (
  language: SupportedLanguage,
  referentials?: ReferentialsResponseDto,
): SelectOption[] =>
  referentials?.regions?.map((region) => ({
    value: String(region.id),
    label: getLocalizedLabel(region.name, language) || '',
  })) ?? [];

const buildStatusOptions = (t: TFunction): SelectOption[] => [
  {
    value: ReportsFindAllStatus.published,
    label: t('filters.options.status.published'),
  },
  {
    value: ReportsFindAllStatus.pendingRRA,
    label: t('filters.options.status.pendingRra'),
  },
  {
    value: ReportsFindAllStatus.publishedRRA,
    label: t('filters.options.status.publishedRra'),
  },
  {
    value: ReportsFindAllStatus.draft,
    label: t('filters.options.status.draft'),
  },
];

const buildAnimalTypeOptions = (t: TFunction): FormOption[] =>
  toToggleOptions(t, [
    {
      value: ReportsFindAllAnimalType.vertebrate,
      label: t('reportCreation.step1.options.animalType.vertebrate'),
    },
    {
      value: ReportsFindAllAnimalType.invertebrate,
      label: t('reportCreation.step1.options.animalType.invertebrate'),
    },
  ]);

const buildWildTypeOptions = (t: TFunction): FormOption[] =>
  toToggleOptions(t, [
    {
      value: ReportsFindAllWildType.freeRoaming,
      label: t('reportCreation.step1.options.wildtype.freeRoaming'),
    },
    {
      value: ReportsFindAllWildType.captive,
      label: t('reportCreation.step1.options.wildtype.captive'),
    },
    {
      value: ReportsFindAllWildType.feral,
      label: t('reportCreation.step1.options.wildtype.feral'),
    },
  ]);

const buildSurveillanceOptions = (t: TFunction): SelectOption[] => [
  {
    value: ReportsFindAllSurveillanceType.active,
    label: t('reportCreation.step2.options.surveillanceType.active'),
  },
  {
    value: ReportsFindAllSurveillanceType.passive,
    label: t('reportCreation.step2.options.surveillanceType.passive'),
  },
  {
    value: ReportsFindAllSurveillanceType.other,
    label: t('reportCreation.step2.options.surveillanceType.other'),
  },
];

const buildYesNoOptions = (t: TFunction): FormOption[] =>
  toToggleOptions(t, [
    {
      value: 'true',
      label: t('reportCreation.step2.options.yesNo.yes'),
    },
    {
      value: 'false',
      label: t('reportCreation.step2.options.yesNo.no'),
    },
  ]);

const buildCategoryOptions = (t: TFunction): SelectOption[] => [
  {
    value: ReportsFindAllCategoryOfTest.direct,
    label: t('reportCreation.step3.options.category.direct'),
  },
  {
    value: ReportsFindAllCategoryOfTest.indirect,
    label: t('reportCreation.step3.options.category.indirect'),
  },
  {
    value: ReportsFindAllCategoryOfTest.observation,
    label: t('reportCreation.step3.options.category.observation'),
  },
  {
    value: ReportsFindAllCategoryOfTest.others,
    label: t('reportCreation.step3.options.category.others'),
  },
];

const buildGeneralInformationSection = (
  t: TFunction,
  language: SupportedLanguage,
  referentials?: ReferentialsResponseDto,
): ReportFiltersSection => {
  const countryOptions = buildCountryOptions(language, referentials);
  const regionOptions = buildRegionOptions(language, referentials);

  return {
    title: t('filters.sections.generalInformation'),
    fields: [
      {
        type: 'text',
        id: 'code',
        label: t('filters.fields.reportName'),
        placeholder: t('filters.placeholders.reportName'),
      },
      {
        type: 'multi-select',
        id: 'countryCode',
        label: t('filters.fields.country'),
        placeholder: t('filters.placeholders.country'),
        options: countryOptions,
      },
      {
        type: 'multi-select',
        id: 'regionId',
        label: t('filters.fields.region'),
        placeholder: t('filters.placeholders.region'),
        options: regionOptions,
      },
      {
        type: 'date-range',
        idFrom: 'detectionDateFrom',
        idTo: 'detectionDateTo',
        label: t('filters.fields.dateOfDetection'),
        placeholder: t('filters.placeholders.dateRange'),
      },
      {
        type: 'multi-select',
        id: 'status',
        label: t('filters.fields.status'),
        placeholder: t('filters.placeholders.status'),
        options: buildStatusOptions(t),
      },
    ],
  };
};

const buildAnimalSection = (t: TFunction): ReportFiltersSection => ({
  title: t('filters.sections.animal'),
  fields: [
    {
      type: 'toggle-group',
      id: 'animalType',
      label: t('filters.fields.animalType'),
      options: buildAnimalTypeOptions(t),
    },
    {
      type: 'text',
      id: 'specie',
      label: t('filters.fields.species'),
      placeholder: t('filters.placeholders.species'),
    },
    {
      type: 'toggle-group',
      id: 'wildType',
      label: t('filters.fields.wildType'),
      options: buildWildTypeOptions(t),
    },
  ],
});

const buildSurveillanceSection = (
  t: TFunction,
  language: SupportedLanguage,
  referentials?: ReferentialsResponseDto,
): ReportFiltersSection => {
  const etiologyTypeOptions = buildEtiologyTypeOptions(
    referentials?.etiologyTypes,
    language,
  );

  const etiologicalAgentOptions = buildEtiologicalAgentOptions(
    referentials?.etiologicalAgents,
    language,
  );
  const etiologicalAgentOptionsByType =
    buildEtiologicalAgentOptionsByEtiologyType(
      referentials?.etiologicalAgents,
      language,
    );
  const hasDynamicEtiologicalAgentOptions =
    Object.keys(etiologicalAgentOptionsByType).length > 0;

  return {
    title: t('filters.sections.surveillanceAndEtiology'),
    fields: [
      {
        type: 'multi-select',
        id: 'surveillanceType',
        label: t('filters.fields.surveillanceType'),
        placeholder: t('filters.placeholders.surveillanceType'),
        options: buildSurveillanceOptions(t),
      },
      {
        type: 'toggle-group',
        id: 'knownCause',
        label: t('filters.fields.knownCause'),
        options: buildYesNoOptions(t),
      },
      {
        type: 'multi-select',
        id: 'etiologyTypeCode',
        label: t('filters.fields.etiologyType'),
        placeholder: t('filters.placeholders.etiologyType'),
        options: etiologyTypeOptions,
      },
      {
        type: 'multi-select',
        id: 'etiologicalAgentCode',
        label: t('filters.fields.etiologicalAgent'),
        placeholder: t('filters.placeholders.etiologicalAgent'),
        options: etiologicalAgentOptions,
        dynamicOptions: hasDynamicEtiologicalAgentOptions
          ? {
              dependsOn: 'etiologyTypeCode',
              optionsByValue: etiologicalAgentOptionsByType,
              showAllWhenDependencyEmpty: true,
            }
          : undefined,
      },
    ],
  };
};

const buildDiagnosticSection = (
  t: TFunction,
  language: SupportedLanguage,
  referentials?: ReferentialsResponseDto,
): ReportFiltersSection => {
  const sampleTypeOptions = buildSampleTypeOptions(
    referentials?.sampleTypes,
    language,
  );

  return {
    title: t('filters.sections.diagnosticProcedure'),
    fields: [
      {
        type: 'multi-select',
        id: 'sampleTypeCode',
        label: t('filters.fields.sampleType'),
        placeholder: t('filters.placeholders.sampleType'),
        options: sampleTypeOptions,
      },
      {
        type: 'multi-select',
        id: 'categoryOfTest',
        label: t('filters.fields.categoryOfTest'),
        placeholder: t('filters.placeholders.categoryOfTest'),
        options: buildCategoryOptions(t),
      },
      {
        type: 'toggle-group',
        id: 'hasDiagnosticProcedure',
        label: t('filters.fields.diagnosticProcedure'),
        options: buildYesNoOptions(t),
      },
    ],
  };
};

export const buildReportFiltersConfig = (
  t: TFunction,
  language: SupportedLanguage,
  referentials?: ReferentialsResponseDto,
): ReportFiltersConfig => ({
  sections: [
    buildGeneralInformationSection(t, language, referentials),
    buildAnimalSection(t),
    buildSurveillanceSection(t, language, referentials),
    buildDiagnosticSection(t, language, referentials),
  ],
});
